import type { PaletteMode, PaletteOptions } from "@mui/material/styles";

/**
 * A hand-rolled, Mantis-inspired light palette (docs/337-ADMIN-MANTIS-MIGRATION.md's own
 * "RECONSTRUCT" allowance): the real Mantis Free `themes/palette.js` computes its palette from
 * `@ant-design/colors`' `presetPalettes` + a bespoke `ThemeOption`/`colorUtils` pipeline that also
 * feeds MUI's newer CSS-variables (`theme.vars.*`) machinery + a module-augmented `lighter`/
 * `darker` tonal-palette shape — a genuinely deep dependency chain this "half the work"
 * evaluation build deliberately did NOT vendor (see the folder README's "How Mantis was
 * vendored" section) to keep the bundle lean and avoid pulling in a whole second design-token
 * system for a look/feel evaluation. Instead: the SAME blue/indigo family Mantis's own default
 * preset ("blue," via Ant Design's own published blue scale) uses, hand-picked to match — same
 * visual identity, standard MUI `PaletteColor` shape, none of the extra machinery.
 */
export function buildPalette(mode: PaletteMode): PaletteOptions {
  if (mode === "dark") {
    return {
      mode,
      primary: { light: "#69b1ff", main: "#4096ff", dark: "#1677ff", contrastText: "#ffffff" },
      secondary: { light: "#d9d9d9", main: "#bfbfbf", dark: "#8c8c8c", contrastText: "#141414" },
      success: { light: "#95de64", main: "#73d13d", dark: "#389e0d", contrastText: "#141414" },
      warning: { light: "#ffd666", main: "#ffc53d", dark: "#d48806", contrastText: "#141414" },
      error: { light: "#ff7875", main: "#ff4d4f", dark: "#cf1322", contrastText: "#ffffff" },
      info: { light: "#69c0ff", main: "#40a9ff", dark: "#096dd9", contrastText: "#141414" },
      text: { primary: "#f0f0f0", secondary: "#bfbfbf", disabled: "#737373" },
      divider: "#303030",
      background: { paper: "#1f1f1f", default: "#141414" },
    };
  }

  return {
    mode,
    primary: { light: "#69b1ff", main: "#1677ff", dark: "#0958d9", contrastText: "#ffffff" },
    secondary: { light: "#8c8c8c", main: "#595959", dark: "#262626", contrastText: "#ffffff" },
    success: { light: "#b7eb8f", main: "#52c41a", dark: "#237804", contrastText: "#ffffff" },
    warning: { light: "#ffe58f", main: "#faad14", dark: "#ad6800", contrastText: "#262626" },
    error: { light: "#ffa39e", main: "#f5222d", dark: "#a8071a", contrastText: "#ffffff" },
    info: { light: "#91d5ff", main: "#1890ff", dark: "#0050b3", contrastText: "#ffffff" },
    text: { primary: "#262626", secondary: "#8c8c8c", disabled: "#bfbfbf" },
    divider: "#f0f0f0",
    background: { paper: "#ffffff", default: "#fafafb" },
  };
}
